import React, { useState } from "react";
import { Box, Grid, Container, Typography, Button, Modal, useTheme } from "@mui/material";
import Header from "../shared/header";
import NotificationsSettings from "../notifications/notifications-settings";
import LinkButton from "../shared/link-button";
import { Lock, Update, Assignment, Close } from "@mui/icons-material";
import Can from "../../casl/Can";
import { tokens } from "../layout/theme";
import { userFullName, userEmailVerifiedAt, formatDateToGermanDate } from "../../utils/helper";

/**
 * A component that displays the settings page of the application.
 * Features include:
 * - Notification settings
 * - Password change option
 * - Microbiological settings (with permission check)
 * - AGB acceptance modal trigger
 * - Responsive layout
 * - Footer with version information
 *
 * @component
 * @returns {JSX.Element} The rendered settings page
 *
 * @example
 * ```tsx
 * <SettingsPage />
 * ```
 */
const SettingsPage = () => {
  /** Controls whether the AGB modal is open */
  const [agbModalOpen, setAgbModalOpen] = useState(false);

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  return (
    <>
      <Box
        ml="20px"
        mt="65px"
        sx={{
          display: "flex",
          flexDirection: "column",
          minHeight: "93vh",
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Header title="Einstellungen" subtitle="" />
        </Box>
        <Grid container spacing={3}>
          <Grid item md={12} xs={12}>
            <NotificationsSettings />
          </Grid>
          <Grid item md={12} xs={12}>
            <LinkButton
              color="warning"
              startIcon={<Lock />}
              label="Mein Kennwort ändern"
              to="change-password"
            />
          </Grid>
          <Can I="save" a="Micro Settings">
            <Grid item md={12} xs={12}>
              <LinkButton
                color="warning"
                startIcon={<Update />}
                label="UEG Standarteinstellungen ändern"
                to="microbiological-settings"
              />
            </Grid>
          </Can>
          {/* AGB acceptance button - opens the AGB info modal */}
          <Grid item md={12} xs={12}>
            <Button
              color="success"
              variant="contained"
              size="small"
              startIcon={<Assignment />}
              onClick={() => setAgbModalOpen(true)}
              sx={{
                fontSize: "1rem",
                textTransform: "none",
              }}
            >
              AGB akzeptiert
            </Button>
          </Grid>
        </Grid>

        {/* AGB info modal */}
        <Modal
          open={agbModalOpen}
          onClose={() => setAgbModalOpen(false)}
        >
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              bgcolor: colors.primary[400],
              borderRadius: 2,
              boxShadow: 24,
              p: 4,
              minWidth: 300,
              maxWidth: 500,
            }}
          >
            {/* Close button in top-right corner */}
            <Box display="flex" justifyContent="flex-end">
              <Button
                onClick={() => setAgbModalOpen(false)}
                sx={{ minWidth: 0, p: 0, color: colors.primary[100] }}
              >
                <Close />
              </Button>
            </Box>
            <Typography variant="body1" mt={1} fontSize="1.1rem">
              Allgemeine Geschäftsbedingungen sind von{" "}
              <strong>{userFullName}</strong> am{" "}
              <strong>{userEmailVerifiedAt ? formatDateToGermanDate(new Date(userEmailVerifiedAt)) : '-'}</strong> akzeptiert worden.
            </Typography>
          </Box>
        </Modal>

        <Box
          component="footer"
          sx={{
            py: 3,
            px: 2,
            mt: "auto",
          }}
        >
          <Container maxWidth="sm">
            <Typography variant="body2" color="text.secondary" align="center">
              {"Copyright © "}
              {"Version - 0.2.2.20260827a"}
              {"."}
            </Typography>
          </Container>
        </Box>
      </Box>
    </>
  );
};

export default SettingsPage;